import { Button, DatePicker, Modal } from '@components/common';
import { DropdownSelect } from '@components/common/DropdownSelect';
import { FormInput, FormTextarea } from '@components/common/FormField';
import { useCountries } from '@hooks/accommodation';
import { AccommodationClient } from '@/renderer/types/accommodation';
import React, { useState } from 'react';

const todayIso = () => new Date().toISOString().slice(0, 10);

interface GuestEditorFormState {
  first_name: string;
  last_name: string;
  email: string;
  contact: string;
  address: string;
  tin_number: string;
  country_id: string;
  date_of_birth: string | null;
  id_type: string;
  id_number: string;
  accommodation_notes: string;
}

export interface GuestEditorSubmitPayload {
  first_name: string;
  last_name: string;
  email: string | null;
  contact: string | null;
  address: string | null;
  tin_number: string | null;
  country_id: number | null;
  date_of_birth: string | null;
  id_type: string | null;
  id_number: string | null;
  accommodation_notes: string | null;
}

interface GuestEditorModalProps {
  client: AccommodationClient;
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (form: GuestEditorSubmitPayload) => void;
}

const toFormState = (client: AccommodationClient): GuestEditorFormState => ({
  first_name: client.first_name,
  last_name: client.last_name,
  email: client.email || '',
  contact: client.contact || '',
  address: client.address || '',
  tin_number: client.tin_number || '',
  country_id: client.country_id != null ? String(client.country_id) : '',
  date_of_birth: client.date_of_birth,
  id_type: client.id_type || '',
  id_number: client.id_number || '',
  accommodation_notes: client.accommodation_notes || '',
});

export const GuestEditorModal: React.FC<GuestEditorModalProps> = ({
  client,
  isSaving,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState<GuestEditorFormState>(toFormState(client));
  const { data: countries = [], isLoading: isLoadingCountries } =
    useCountries();

  const set = <K extends keyof GuestEditorFormState>(
    key: K,
    value: GuestEditorFormState[K],
  ) => setForm((current) => ({ ...current, [key]: value }));

  const countryOptions = countries.map((country) => ({
    value: String(country.id),
    label: country.name,
  }));

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit({
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: form.email.trim() || null,
      contact: form.contact.trim() || null,
      address: form.address.trim() || null,
      tin_number: form.tin_number.trim() || null,
      country_id: form.country_id ? Number(form.country_id) : null,
      date_of_birth: form.date_of_birth || null,
      id_type: form.id_type.trim() || null,
      id_number: form.id_number.trim() || null,
      accommodation_notes: form.accommodation_notes.trim() || null,
    });
  };

  return (
    <Modal isOpen onClose={onClose} title="Edit Guest Details" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="First name"
            required
            value={form.first_name}
            onChange={(event) => set('first_name', event.target.value)}
          />
          <FormInput
            label="Last name"
            required
            value={form.last_name}
            onChange={(event) => set('last_name', event.target.value)}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="Email"
            type="email"
            value={form.email}
            onChange={(event) => set('email', event.target.value)}
            placeholder="e.g. guest@example.com"
          />
          <FormInput
            label="Contact"
            value={form.contact}
            onChange={(event) => set('contact', event.target.value)}
            placeholder="e.g. +256700000000"
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="Address"
            value={form.address}
            onChange={(event) => set('address', event.target.value)}
          />
          <FormInput
            label="TIN"
            value={form.tin_number}
            onChange={(event) => set('tin_number', event.target.value)}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <DropdownSelect
            label="Country"
            value={form.country_id}
            onChange={(value) => set('country_id', value)}
            options={countryOptions}
            searchable
            disabled={isLoadingCountries}
            placeholder="Select country"
          />
          <DatePicker
            mode="single"
            label="Date of birth"
            value={form.date_of_birth}
            onChange={(iso) => set('date_of_birth', iso)}
            maxDate={todayIso()}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="ID type"
            value={form.id_type}
            onChange={(event) => set('id_type', event.target.value)}
            placeholder="e.g. Passport"
          />
          <FormInput
            label="ID number"
            value={form.id_number}
            onChange={(event) => set('id_number', event.target.value)}
          />
        </div>

        <FormTextarea
          label="Management notes"
          value={form.accommodation_notes}
          onChange={(event) => set('accommodation_notes', event.target.value)}
          placeholder="Notes shown in the guest's preview panel"
          rows={4}
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSaving}>
            Save changes
          </Button>
        </div>
      </form>
    </Modal>
  );
};
